import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { Spinner } from './components/ui/Spinner';
// ── EAGER: public + owner critical path (kept in the entry chunk so first paint
//    is fast). Login → Onboarding → Dashboard for owners; /q/:token → the PIC
//    questionnaire (via PicLanding). These never pull the chart/admin code. ──
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Dashboard } from './pages/Dashboard';
import { IntakeUpload } from './pages/IntakeUpload';
import { Documents } from './pages/Documents';
import { PicLanding } from './pages/PicLanding';
import { OwnerVerification } from './pages/OwnerVerification';

// ── LAZY: internal/admin console + chart-heavy owner surfaces. These pull the
//    Recharts-equivalent chart code (components/Chart) and the internal review
//    UIs; a public Owner should never download them. Each becomes its own chunk
//    (route-based code-split) loaded on demand behind a <Suspense> fallback. ──
const Report = lazy(() => import('./pages/Report').then((m) => ({ default: m.Report })));
const Coaching = lazy(() => import('./pages/Coaching').then((m) => ({ default: m.Coaching })));
const QuestionnaireReview = lazy(() =>
  import('./pages/QuestionnaireReview').then((m) => ({ default: m.QuestionnaireReview })),
);
const Distribution = lazy(() =>
  import('./pages/Distribution').then((m) => ({ default: m.Distribution })),
);
const ReportReview = lazy(() =>
  import('./pages/ReportReview').then((m) => ({ default: m.ReportReview })),
);
const CoachingManage = lazy(() =>
  import('./pages/CoachingManage').then((m) => ({ default: m.CoachingManage })),
);
const AdminOverview = lazy(() =>
  import('./pages/admin/Overview').then((m) => ({ default: m.AdminOverview })),
);
const AdminClients = lazy(() =>
  import('./pages/admin/Clients').then((m) => ({ default: m.AdminClients })),
);
const AdminQuestionBank = lazy(() =>
  import('./pages/admin/QuestionBank').then((m) => ({ default: m.AdminQuestionBank })),
);
const AdminAnalytics = lazy(() =>
  import('./pages/admin/Analytics').then((m) => ({ default: m.AdminAnalytics })),
);
const AdminAuditLog = lazy(() =>
  import('./pages/admin/AuditLog').then((m) => ({ default: m.AdminAuditLog })),
);

/**
 * Full-screen fallback for lazily-loaded route chunks. Reuses the shared
 * <Spinner> (Lucide loader, which respects reduced-motion via Tailwind's
 * `motion-reduce` handling at the CSS layer) for a consistent loading state.
 */
function RouteFallback() {
  return (
    <div className="h-screen">
      <Spinner />
    </div>
  );
}

function Protected({
  children,
  requireOnboarded = false,
  requireInternal = false,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireOnboarded?: boolean;
  /** CR6 — gate to Nine Co internal staff (coach/admin). Owners → home. */
  requireInternal?: boolean;
  /** CR6 — gate to a global admin (analytics / audit). Coaches → admin home. */
  requireAdmin?: boolean;
}) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen">
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Owners must complete company onboarding before using the app.
  if (requireOnboarded && user.role === 'owner' && !user.companyId) {
    return <Navigate to="/onboarding" replace />;
  }
  // Internal console — owners/PIC can never reach it (server is authoritative;
  // this is the client-side mirror so they bounce to their own home).
  if (requireInternal && user.role !== 'coach' && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  // Admin-only governance surfaces (analytics / audit) — a coach bounces to the
  // admin home (the server hard-gates the data with 403 regardless).
  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

/**
 * Root "/" — internal staff (coach/admin) land on the admin console (their home);
 * owners get the Owner dashboard. Keeps a single canonical home per role.
 */
function HomeRoute() {
  const { user } = useAuth();
  if (user && (user.role === 'coach' || user.role === 'admin')) {
    return <Navigate to="/admin" replace />;
  }
  return <Dashboard />;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/q/:token" element={<PicLanding />} />
            <Route
              path="/onboarding"
              element={
                <Protected>
                  <Onboarding />
                </Protected>
              }
            />
            <Route
              path="/"
              element={
                <Protected requireOnboarded>
                  <HomeRoute />
                </Protected>
              }
            />
            <Route
              path="/intake/upload"
              element={
                <Protected requireOnboarded>
                  <IntakeUpload />
                </Protected>
              }
            />
            <Route
              path="/documents"
              element={
                <Protected requireOnboarded>
                  <Documents />
                </Protected>
              }
            />
            {/* CR3 — Owner verification round (round 2), answered in-app. */}
            <Route
              path="/verify"
              element={
                <Protected requireOnboarded>
                  <OwnerVerification />
                </Protected>
              }
            />
            {/* CR4 — Owner-facing GCU report (read-only; gated to status=sent
                server-side). Reads the owner's own company via the session. */}
            <Route
              path="/report"
              element={
                <Protected requireOnboarded>
                  <Report />
                </Protected>
              }
            />
            {/* CR5 — Owner-facing coaching home: recommendations (cr5a) +
                progress (cr5b). Reads the owner's own company via the session;
                access is enforced server-side (assertCanAccessCompany → 403). */}
            <Route
              path="/coaching"
              element={
                <Protected requireOnboarded>
                  <Coaching />
                </Protected>
              }
            />
            {/* CR3 — internal (coach/admin) questionnaire review + distribution.
                The company is identified by the route param; access is enforced
                server-side (assertCanAccessCompany → 403 on cross-tenant). */}
            <Route
              path="/companies/:companyId/review"
              element={
                <Protected>
                  <QuestionnaireReview />
                </Protected>
              }
            />
            <Route
              path="/companies/:companyId/distribution"
              element={
                <Protected>
                  <Distribution />
                </Protected>
              }
            />
            {/* CR4 — internal (coach/admin) report review + finalize + send. */}
            <Route
              path="/companies/:companyId/report-review"
              element={
                <Protected>
                  <ReportReview />
                </Protected>
              }
            />
            {/* CR5 — internal (coach/admin) coaching console: review/adjust/offer
                recommendations + manage engagements/sessions/progress. The company
                is identified by the route param; access is enforced server-side. */}
            <Route
              path="/companies/:companyId/coaching"
              element={
                <Protected>
                  <CoachingManage />
                </Protected>
              }
            />
            {/* CR6 — Nine Co INTERNAL admin console (slate surface). The whole
                /admin tree is gated to internal staff (coach/admin); the server is
                authoritative (the /admin router requires requireRole('coach',
                'admin')). Coach = assigned-scoped; admin = all + governance. */}
            <Route
              path="/admin"
              element={
                <Protected requireInternal>
                  <AdminOverview />
                </Protected>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <Protected requireInternal>
                  <AdminClients />
                </Protected>
              }
            />
            <Route
              path="/admin/question-bank"
              element={
                <Protected requireInternal>
                  <AdminQuestionBank />
                </Protected>
              }
            />
            {/* Analytics + Audit are cross-company governance → admin-only. */}
            <Route
              path="/admin/analytics"
              element={
                <Protected requireInternal requireAdmin>
                  <AdminAnalytics />
                </Protected>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <Protected requireInternal requireAdmin>
                  <AdminAuditLog />
                </Protected>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
